import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyService } from 'src/app/core/services/currency.service';
import {
    formatCurrencyValue,
    CurrencyFormatOptions
} from 'src/app/core/utils/currency.utils';

/**
 * Pipe para formatear valores monetarios con posicionamiento correcto del símbolo.
 *
 * Uso:
 * {{ 1234.56 | currencyFormat }}                           // Usa moneda actual
 * {{ 1234.56 | currencyFormat:'USD' }}                     // Moneda específica
 * {{ 1234.56 | currencyFormat:'EUR':'es-ES' }}             // Con locale
 *
 * El pipe automáticamente:
 * - Detecta la posición correcta del símbolo (izquierda/derecha)
 * - Aplica formateo de número según locale
 * - Maneja valores null/undefined/negativos
 * - Se actualiza cuando cambia la moneda del usuario
 */
@Pipe({
    name: 'currencyFormat',
    pure: false
})
export class CurrencyFormatPipe implements PipeTransform {
    private currentCurrencyCode = 'EUR';

    constructor(private currencyService: CurrencyService) {
        this.currencyService.currencyCode$.subscribe(code => {
            this.currentCurrencyCode = code;
        });
    }

    transform(
        amount: number | string | null | undefined,
        currencyCode?: string | null,
        locale?: string | null,
        minimumFractionDigits: number = 2,
        maximumFractionDigits: number = 2
    ): string {
        let numericAmount: number | null | undefined = amount as number | null | undefined;
        if (typeof amount === 'string') {
            const normalized = amount
                .replace(/\s+/g, '')
                .replace(/,/g, '.')
                .replace(/[^0-9.\-]/g, '');
            const parsed = Number.parseFloat(normalized);
            numericAmount = Number.isNaN(parsed) ? null : parsed;
        }

        const code = currencyCode ?? this.currentCurrencyCode;

        const formatOptions: Omit<CurrencyFormatOptions, 'amount'> = {
            currencyCode: code,
            locale: locale,
            minimumFractionDigits,
            maximumFractionDigits,
            showSymbol: true,
            symbolPosition: 'auto'
        };

        return formatCurrencyValue(numericAmount, formatOptions);
    }
}
